import { normalizeUiLocale } from "@/lib/i18n/uiLocale";

export interface BackendUiLanguage {
  id?: number | null;
  code?: string | null;
  iso?: string | null;
  name?: string | null;
  is_default?: number | boolean | null;
  weight?: number | null;
}

export interface UiLanguageOption {
  locale: string;
  iso: string | null;
  label: string;
  short: string;
  isDefault: boolean;
}

const FALLBACK_UI_LANGUAGE_OPTIONS: UiLanguageOption[] = [
  {
    locale: "kk",
    iso: "kk_KZ",
    label: "Қазақша",
    short: "KZ",
    isDefault: false,
  },
  {
    locale: "en",
    iso: "en_GB",
    label: "English",
    short: "EN",
    isDefault: false,
  },
  {
    locale: "ru",
    iso: "ru_RU",
    label: "Русский",
    short: "RU",
    isDefault: false,
  },
];

export const SYSTEMS247_UI_LANGUAGES: BackendUiLanguage[] = [
  {
    id: 2,
    code: "en",
    iso: "en_GB",
    name: "English",
    is_default: 0,
    weight: null,
  },
  {
    id: 3,
    code: "fr-CA",
    iso: "fr_CA",
    name: "Français (Canada)",
    is_default: 0,
    weight: null,
  },
  {
    code: "ar",
    iso: "ar_SA",
    name: "العربية",
    is_default: 0,
    weight: null,
  },
  {
    code: "ur",
    iso: "ur_PK",
    name: "اردو",
    is_default: 0,
    weight: null,
  },
  {
    code: "hi",
    iso: "hi_IN",
    name: "हिन्दी",
    is_default: 0,
    weight: null,
  },
  {
    code: "ml",
    iso: "ml_IN",
    name: "മലയാളം",
    is_default: 0,
    weight: null,
  },
];

function getFallbackLabel(locale: string): string {
  switch (locale) {
    case "kk":
      return "Қазақша";
    case "en":
      return "English";
    case "fr-CA":
      return "Français (Canada)";
    case "ru":
      return "Русский";
    case "ar":
      return "العربية";
    case "ur":
      return "اردو";
    case "hi":
      return "हिन्दी";
    case "ml":
      return "മലയാളം";
    default:
      return locale;
  }
}

export function getUiLanguageShortLabel(locale: string): string {
  const normalized = normalizeUiLocale(locale) ?? locale;

  if (normalized === "kk") return "KZ";
  if (normalized === "ru") return "RU";
  if (normalized === "en") return "EN";

  return normalized.toUpperCase();
}

export function getFallbackUiLanguageOptions(): UiLanguageOption[] {
  return FALLBACK_UI_LANGUAGE_OPTIONS.map((item) => ({ ...item }));
}

function toUiLanguageOption(input: BackendUiLanguage): UiLanguageOption | null {
  const locale = normalizeUiLocale(input.code ?? input.iso ?? null);
  if (!locale) return null;

  const label = input.name?.trim() || getFallbackLabel(locale);

  return {
    locale,
    iso: input.iso?.trim() || null,
    label,
    short: getUiLanguageShortLabel(locale),
    isDefault: input.is_default === true || input.is_default === 1,
  };
}

export function buildUiLanguageOptions(
  input: BackendUiLanguage[] | null | undefined
): UiLanguageOption[] {
  if (!Array.isArray(input) || input.length === 0) {
    return getFallbackUiLanguageOptions();
  }

  const options = normalizeUiLanguageOptions(input);
  if (options.length === 0) {
    return getFallbackUiLanguageOptions();
  }

  return options;
}

export function normalizeUiLanguageOptions(
  input: BackendUiLanguage[] | null | undefined
): UiLanguageOption[] {
  if (!Array.isArray(input) || input.length === 0) {
    return [];
  }

  const deduped = new Map<string, UiLanguageOption>();

  for (const item of input) {
    const option = toUiLanguageOption(item);
    if (!option) continue;

    const existing = deduped.get(option.locale);
    if (!existing || option.isDefault) {
      deduped.set(option.locale, option);
    }
  }

  return Array.from(deduped.values());
}

export function getUiLanguageOptionByLocale(
  options: UiLanguageOption[],
  locale: string | null | undefined
): UiLanguageOption | null {
  const normalized = normalizeUiLocale(locale);
  if (!normalized) return null;

  return options.find((option) => option.locale === normalized) ?? null;
}
